import { isString } from 'fitify-types/src/type-guards/common'
import type { Url } from 'next/dist/shared/lib/router/router'
import { type NextRouter, useRouter as useNextRouter } from 'next/router'
import { useMemo } from 'react'

import { usePromoContext } from '../contexts/PromoContext'
import { emitOnboardingPageTransitionEvent } from '../utils/animation-events'
import { encodeQuery } from '../utils/query'

/**
 * Wraps Next.js `useRouter` with onboarding-specific behavior.
 *
 * - `resolvedPathname`: like `router.pathname`, but with `[param]` segments
 *   substituted with their actual values from `router.query`. Use this when
 *   you need the concrete path (e.g. for analytics or matching) rather than
 *   the dynamic route template.
 * - `isReady`: extends `router.isReady` to also wait for the promo context
 *   to be initialized, so consumers can safely read promo-dependent state.
 * - `push` / `replace`: automatically append the active `promo` query
 *   parameter to the target URL (unless it's the default promo), so the
 *   promo code persists across navigation. Also emits the onboarding page
 *   transition event on `push`.
 */
export const useRouter = () => {
  const router = useNextRouter()
  const { isDefaultPromo, isInitialized, promoCode } = usePromoContext()

  const resolvedPathname = useMemo(() => {
    return Object.entries(router.query).reduce((path, [key, value]) => {
      if (!isString(value)) {
        return path
      }
      return path.replace(`[${key}]`, value)
    }, router.pathname)
  }, [router.pathname, router.query])

  return useMemo(() => {
    const addPromoToUrl = (url: Url) => {
      if (isDefaultPromo) {
        return url
      }
      if (typeof url === 'string') {
        const [path, queryString] = url.toString().split('?')
        const query = Object.fromEntries(new URLSearchParams(queryString || ''))
        return encodeQuery(path, { ...query, promo: promoCode })
      } else {
        let newQuery

        if (url.query) {
          if (typeof url.query === 'string') {
            newQuery = `${url.query}&promo=${promoCode}`
          } else {
            newQuery = { ...url.query, promo: promoCode }
          }
        } else {
          newQuery = { promo: promoCode }
        }
        return { ...url, query: newQuery }
      }
    }

    const push: NextRouter['push'] = (url, as, options) => {
      emitOnboardingPageTransitionEvent()
      if (promoCode) {
        return router.push(addPromoToUrl(url), as && addPromoToUrl(as), options)
      }
      return router.push(url, as, options)
    }

    const replace: NextRouter['replace'] = (url, as, options) => {
      if (promoCode) {
        return router.replace(
          addPromoToUrl(url),
          as && addPromoToUrl(as),
          options
        )
      }
      return router.replace(url, as, options)
    }

    return {
      ...router,
      push,
      replace,
      resolvedPathname,
      isReady: router.isReady && isInitialized,
    }
  }, [isDefaultPromo, isInitialized, promoCode, resolvedPathname, router])
}
